// ============ FEES PAGE ============
import { useState } from 'react';
import { useFetch, useClasses, useSubmit } from '../hooks';
import { Table, Modal, PageHeader, Select, FormField, StatusBadge, PageHeader as PH } from '../components/UI';
import { Plus, Download, DollarSign, CheckCircle } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export function Fees() {
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [form, setForm] = useState({ student_id: '', fee_type: 'Tuition Fee', amount: '', due_date: '' });
  const [bulkForm, setBulkForm] = useState({ class_id: '', fee_type: 'Tuition Fee', amount: '', due_date: '' });
  const { data: fees, loading, refetch } = useFetch(`/fees?status=${statusFilter}`);
  const { classes } = useClasses();
  const { loading: saving, submit } = useSubmit();

  const STATUS_OPTS = [{ value: 'pending', label: 'Pending' }, { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' }];
  const FEE_TYPES = ['Tuition Fee', 'Exam Fee', 'Library Fee', 'Sports Fee', 'Annual Charges', 'Transport Fee', 'Other'].map(v => ({ value: v, label: v }));
  const classOpts = classes.map(c => ({ value: c.id, label: `${c.name} - ${c.section}` }));

  const markPaid = async (id) => {
    await submit(() => api.put(`/fees/${id}/pay`, { payment_method: 'Cash' }), 'Payment recorded!');
    refetch();
  };

  const downloadCSV = () => {
    window.open(`${api.defaults.baseURL}/reports/fees-csv?status=${statusFilter}`, '_blank');
  };

  const totalAmt = fees?.reduce((s, f) => s + parseFloat(f.amount || 0), 0) || 0;
  const paidAmt = fees?.filter(f => f.status === 'paid').reduce((s, f) => s + parseFloat(f.amount || 0), 0) || 0;
  const pendingAmt = fees?.filter(f => f.status !== 'paid').reduce((s, f) => s + parseFloat(f.amount || 0), 0) || 0;

  const columns = [
    { label: 'Student', render: r => <div><div style={{ fontWeight: 700 }}>{r.full_name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.class_name} {r.section}</div></div> },
    { label: 'Fee Type', key: 'fee_type' },
    { label: 'Amount', render: r => <span style={{ fontWeight: 700 }}>₹{parseFloat(r.amount).toLocaleString()}</span> },
    { label: 'Due Date', key: 'due_date' },
    { label: 'Paid Date', key: 'paid_date' },
    { label: 'Receipt', key: 'receipt_no' },
    { label: 'Status', render: r => <StatusBadge status={r.status} /> },
    { label: 'Action', render: r => r.status !== 'paid'
      ? <button className="btn btn-success btn-sm" onClick={() => markPaid(r.id)}><CheckCircle size={13}/> Mark Paid</button>
      : <span style={{ color: 'var(--secondary)', fontWeight: 600, fontSize: '0.8rem' }}>✓ Paid</span>
    },
  ];

  return (
    <div>
      <PH title="Fee Management" subtitle={`Total: ₹${totalAmt.toLocaleString()} | Collected: ₹${paidAmt.toLocaleString()} | Pending: ₹${pendingAmt.toLocaleString()}`}
        actions={<>
          <Select value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTS} placeholder="All Status" />
          <button className="btn btn-secondary" onClick={downloadCSV}><Download size={15}/> Export CSV</button>
          <button className="btn btn-secondary" onClick={() => setShowBulk(true)}><Plus size={15}/> Bulk Add</button>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={15}/> Add Fee</button>
        </>} />

      <div className="card"><Table columns={columns} data={fees} loading={loading} emptyMsg="No fee records" /></div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Fee Record" size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/fees', form), 'Fee added!');
            if (r.success) { setShowAdd(false); refetch(); }
          }}>{saving ? 'Adding...' : 'Add Fee'}</button></>}>
        <div className="form-grid">
          <FormField label="Student ID" required><input className="form-control" value={form.student_id} onChange={e => setForm(f => ({...f, student_id: e.target.value}))} placeholder="Student DB ID"/></FormField>
          <FormField label="Fee Type"><Select value={form.fee_type} onChange={v => setForm(f => ({...f, fee_type: v}))} options={FEE_TYPES}/></FormField>
          <FormField label="Amount (₹)" required><input className="form-control" type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))}/></FormField>
          <FormField label="Due Date"><input className="form-control" type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date: e.target.value}))}/></FormField>
        </div>
      </Modal>

      <Modal open={showBulk} onClose={() => setShowBulk(false)} title="Bulk Fee - Entire Class" size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setShowBulk(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/fees/bulk', bulkForm), 'Bulk fee applied!');
            if (r.success) { setShowBulk(false); refetch(); }
          }}>{saving ? 'Applying...' : 'Apply to Class'}</button></>}>
        <div className="form-grid">
          <FormField label="Class"><Select value={bulkForm.class_id} onChange={v => setBulkForm(f => ({...f, class_id: v}))} options={classOpts} placeholder="Select Class"/></FormField>
          <FormField label="Fee Type"><Select value={bulkForm.fee_type} onChange={v => setBulkForm(f => ({...f, fee_type: v}))} options={FEE_TYPES}/></FormField>
          <FormField label="Amount (₹)"><input className="form-control" type="number" value={bulkForm.amount} onChange={e => setBulkForm(f => ({...f, amount: e.target.value}))}/></FormField>
          <FormField label="Due Date"><input className="form-control" type="date" value={bulkForm.due_date} onChange={e => setBulkForm(f => ({...f, due_date: e.target.value}))}/></FormField>
        </div>
      </Modal>
    </div>
  );
}

// ============ HOLIDAYS PAGE ============
export function Holidays() {
  const { data: holidays, loading, refetch } = useFetch('/holidays');
  const [form, setForm] = useState({ name: '', date: '', type: 'national', description: '', send_whatsapp: true });
  const [showAdd, setShowAdd] = useState(false);
  const { loading: saving, submit } = useSubmit();
  const TYPE_OPTS = ['national','school','regional','event'].map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));

  const typeColor = { national: 'info', school: 'success', regional: 'purple', event: 'warning' };

  const columns = [
    { label: 'Holiday', render: r => <div><div style={{ fontWeight: 700 }}>{r.name}</div><div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.description}</div></div> },
    { label: 'Date', key: 'date' },
    { label: 'Type', render: r => <StatusBadge status={r.type} /> },
    { label: 'WhatsApp', render: r => r.whatsapp_sent ? <span style={{ color: '#25d366', fontWeight: 600, fontSize: '0.8rem' }}>✓ Sent</span> : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Not sent</span> },
    { label: '', render: r => <button className="btn btn-danger btn-sm" onClick={async () => { await api.delete(`/holidays/${r.id}`); refetch(); }}>Delete</button> },
  ];

  return (
    <div>
      <PH title="Holidays" subtitle="School calendar and holiday management"
        actions={<button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={15}/> Add Holiday</button>} />
      <div className="card"><Table columns={columns} data={holidays} loading={loading} /></div>
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Holiday" size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/holidays', form), 'Holiday added!');
            if (r.success) { setShowAdd(false); refetch(); }
          }}>{saving ? 'Adding...' : 'Add Holiday'}</button></>}>
        <div className="form-grid">
          <FormField label="Name" required><input className="form-control" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}/></FormField>
          <FormField label="Date" required><input className="form-control" type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))}/></FormField>
          <FormField label="Type"><Select value={form.type} onChange={v => setForm(f => ({...f, type: v}))} options={TYPE_OPTS}/></FormField>
          <FormField label="Description"><input className="form-control" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}/></FormField>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, gridColumn: '1/-1' }}>
            <input type="checkbox" checked={form.send_whatsapp} onChange={e => setForm(f => ({...f, send_whatsapp: e.target.checked}))}/> Send WhatsApp notification to all parents
          </label>
        </div>
      </Modal>
    </div>
  );
}

// ============ NOTICES PAGE ============
export function Notices() {
  const { data: notices, loading, refetch } = useFetch('/notices');
  const [form, setForm] = useState({ title: '', content: '', priority: 'normal', target_role: 'all' });
  const [showAdd, setShowAdd] = useState(false);
  const { loading: saving, submit } = useSubmit();
  const PRIORITY_OPTS = ['low','normal','high','urgent'].map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
  const ROLE_OPTS = [{ value: 'all', label: 'All' }, { value: 'teacher', label: 'Teachers' }, { value: 'student', label: 'Students' }, { value: 'parent', label: 'Parents' }];
  const priorityColor = { low: 'neutral', normal: 'info', high: 'warning', urgent: 'danger' };

  const columns = [
    { label: 'Title', render: r => <div><div style={{ fontWeight: 700 }}>{r.title}</div><div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{r.content?.slice(0, 80)}...</div></div> },
    { label: 'Target', render: r => <StatusBadge status={r.target_role} /> },
    { label: 'Priority', render: r => <StatusBadge status={r.priority} /> },
    { label: 'By', key: 'published_by_name' },
    { label: 'Date', render: r => new Date(r.created_at).toLocaleDateString('en-IN') },
    { label: '', render: r => <button className="btn btn-danger btn-sm" onClick={async () => { await api.delete(`/notices/${r.id}`); refetch(); }}>Delete</button> },
  ];

  return (
    <div>
      <PH title="Notices & Announcements" subtitle={`${notices?.length || 0} active notices`}
        actions={<button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={15}/> Post Notice</button>} />
      <div className="card"><Table columns={columns} data={notices} loading={loading} /></div>
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Post New Notice"
        footer={<><button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/notices', form), 'Notice posted!');
            if (r.success) { setShowAdd(false); refetch(); }
          }}>{saving ? 'Posting...' : 'Post Notice'}</button></>}>
        <div className="form-grid">
          <FormField label="Title" required><input className="form-control" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}/></FormField>
          <FormField label="Target Audience"><Select value={form.target_role} onChange={v => setForm(f => ({...f, target_role: v}))} options={ROLE_OPTS}/></FormField>
          <FormField label="Priority"><Select value={form.priority} onChange={v => setForm(f => ({...f, priority: v}))} options={PRIORITY_OPTS}/></FormField>
          <FormField label="Message" required><textarea className="form-control" style={{ gridColumn: '1/-1' }} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))}/></FormField>
        </div>
      </Modal>
    </div>
  );
}

// ============ WHATSAPP PAGE ============
export function WhatsApp() {
  const { data: logs, loading } = useFetch('/whatsapp/logs');
  const [phones, setPhones] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const sendCustom = async () => {
    if (!phones || !message) return toast.error('Fill all fields');
    setSending(true);
    try {
      const phoneArr = phones.split('\n').map(p => p.trim()).filter(Boolean);
      const { data } = await api.post('/whatsapp/send', { phones: phoneArr, message, type: 'custom' });
      toast.success(`Sent: ${data.sent}, Failed: ${data.failed}`);
      setPhones(''); setMessage('');
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const columns = [
    { label: 'Phone', key: 'recipient_phone' },
    { label: 'Type', render: r => <StatusBadge status={r.message_type} /> },
    { label: 'Message', render: r => <span style={{ fontSize: '0.78rem' }}>{r.message?.slice(0, 60)}...</span> },
    { label: 'Status', render: r => <StatusBadge status={r.status} /> },
    { label: 'Time', render: r => new Date(r.created_at).toLocaleString('en-IN') },
  ];

  return (
    <div>
      <PH title="WhatsApp Automation" subtitle="Send automated WhatsApp messages to parents & teachers" />
      <div className="grid-2" style={{ marginBottom: 24 }}>
        <div className="card">
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>📱 Send Custom Message</h3>
          <FormField label="Phone Numbers (one per line)">
            <textarea className="form-control" rows={5} value={phones} onChange={e => setPhones(e.target.value)} placeholder="9876543210&#10;9123456789"/>
          </FormField>
          <FormField label="Message" required>
            <textarea className="form-control" rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="Type your message..."/>
          </FormField>
          <button className="btn btn-whatsapp" style={{ marginTop: 12 }} disabled={sending} onClick={sendCustom}>
            {sending ? 'Sending...' : '💬 Send WhatsApp'}
          </button>
        </div>
        <div className="card">
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>ℹ️ Automation Rules</h3>
          {[
            { t: '✅ Attendance Alert', d: 'Auto-sent when student is marked absent', status: 'Active' },
            { t: '📅 Holiday Notice', d: 'Sent to all parents when holiday is added', status: 'Active' },
            { t: '📢 Notice Alert', d: 'Sent when admin posts notice with WhatsApp option', status: 'Active' },
            { t: '💰 Fee Reminder', d: 'Configured via cron job for overdue fees', status: 'Configure in backend' },
          ].map(r => (
            <div key={r.t} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{r.t}</div>
                <span style={{ fontSize: '0.72rem', background: '#e6f4ea', color: '#0f9d58', padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}>{r.status}</span>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>{r.d}</div>
            </div>
          ))}
          <div style={{ marginTop: 16, padding: '12px', background: '#fef3e2', borderRadius: 'var(--r-sm)', fontSize: '0.8rem', color: '#b35c00' }}>
            ⚙️ Configure WHATSAPP_WEBHOOK_URL in backend .env to activate automation
          </div>
        </div>
      </div>
      <div className="card">
        <h3 style={{ fontWeight: 700, marginBottom: 16 }}>📋 Message Logs</h3>
        <Table columns={columns} data={logs} loading={loading} emptyMsg="No messages sent yet" />
      </div>
    </div>
  );
}
